import { type MetaFunction } from '@remix-run/node'

export const meta: MetaFunction = () => [
	{ title: 'Terms of Service - MinHub' },
	{ name: 'description', content: 'Terms of Service for MinHub' },
]

export default function TermsOfServiceRoute() {
	return (
		<div className="mx-auto max-w-4xl px-4 py-16">
			<h1 className="mb-8 text-4xl font-bold">Terms of Service</h1>
			
			<div className="space-y-6 text-base leading-7">
				<p className="text-muted-foreground mb-6">
					Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
				</p>

				<section className="mb-8">
					<h2 className="text-2xl font-semibold mb-4">1. Acceptance of Terms</h2>
					<p className="mb-4">
						By accessing and using MinHub ("the Service"), you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.
					</p>
				</section>

				<section className="mb-8">
					<h2 className="text-2xl font-semibold mb-4">2. Use License</h2>
					<p className="mb-4">
						Permission is granted to temporarily access the materials on MinHub's website for personal, non-commercial transitory viewing only. This is the grant of a license, not a transfer of title, and under this license you may not:
					</p>
					<ul className="list-disc pl-6 mb-4 space-y-2">
						<li>modify or copy the materials;</li>
						<li>use the materials for any commercial purpose, or for any public display (commercial or non-commercial);</li>
						<li>attempt to decompile or reverse engineer any software contained on MinHub's website;</li>
						<li>remove any copyright or other proprietary notations from the materials; or</li>
						<li>transfer the materials to another person or "mirror" the materials on any other server.</li>
					</ul>
					<p className="mb-4">
						This license shall automatically terminate if you violate any of these restrictions and may be terminated by MinHub at any time.
					</p>
				</section>

				<section className="mb-8">
					<h2 className="text-2xl font-semibold mb-4">3. User Accounts</h2>
					<p className="mb-4">
						You are responsible for maintaining the confidentiality of your account and password and for restricting access to your computer. You agree to accept responsibility for all activities that occur under your account or password.
					</p>
				</section>

				<section className="mb-8">
					<h2 className="text-2xl font-semibold mb-4">4. User Content</h2>
					<p className="mb-4">
						You retain ownership of any content you submit, post, or display on or through the Service ("User Content"). By submitting, posting, or displaying User Content, you grant MinHub a worldwide, non-exclusive, royalty-free license to use, reproduce, modify, adapt, publish, translate, and distribute such User Content solely for the purpose of providing and improving the Service.
					</p>
					<p className="mb-4">
						You are solely responsible for your User Content and the consequences of posting or publishing it. You represent and warrant that you have all necessary rights to grant the license above and that your User Content does not violate any third-party rights.
					</p>
				</section>

				<section className="mb-8">
					<h2 className="text-2xl font-semibold mb-4">5. Prohibited Uses</h2>
					<p className="mb-4">You may not use the Service:</p>
					<ul className="list-disc pl-6 mb-4 space-y-2">
						<li>in any way that violates any applicable federal, state, local, or international law or regulation;</li>
						<li>to transmit, or procure the sending of, any advertising or promotional material without our prior written consent;</li>
						<li>to impersonate or attempt to impersonate MinHub, a MinHub employee, another user, or any other person or entity;</li>
						<li>in any way that infringes upon the rights of others, or in any way is illegal, threatening, fraudulent, or harmful;</li>
						<li>to engage in any other conduct that restricts or inhibits anyone's use or enjoyment of the Service.</li>
					</ul>
				</section>

				<section className="mb-8">
					<h2 className="text-2xl font-semibold mb-4">6. Subscription and Payment</h2>
					<p className="mb-4">
						If you purchase a subscription to the Service, you agree to pay all charges associated with your account. Subscription fees are billed in advance on a recurring basis. You may cancel your subscription at any time, and cancellation will take effect at the end of the current billing period.
					</p>
				</section>

				<section className="mb-8">
					<h2 className="text-2xl font-semibold mb-4">7. Disclaimer</h2>
					<p className="mb-4">
						The materials on MinHub's website are provided on an 'as is' basis. MinHub makes no warranties, expressed or implied, and hereby disclaims and negates all other warranties including, without limitation, implied warranties or conditions of merchantability, fitness for a particular purpose, or non-infringement of intellectual property or other violation of rights.
					</p>
				</section>

				<section className="mb-8">
					<h2 className="text-2xl font-semibold mb-4">8. Limitations</h2>
					<p className="mb-4">
						In no event shall MinHub or its suppliers be liable for any damages (including, without limitation, damages for loss of data or profit, or due to business interruption) arising out of the use or inability to use the materials on MinHub's website, even if MinHub or a MinHub authorized representative has been notified orally or in writing of the possibility of such damage.
					</p>
				</section>

				<section className="mb-8">
					<h2 className="text-2xl font-semibold mb-4">9. Accuracy of Materials</h2>
					<p className="mb-4">
						The materials appearing on MinHub's website could include technical, typographical, or photographic errors. MinHub does not warrant that any of the materials on its website are accurate, complete, or current. MinHub may make changes to the materials contained on its website at any time without notice.
					</p>
				</section>

				<section className="mb-8">
					<h2 className="text-2xl font-semibold mb-4">10. Links</h2>
					<p className="mb-4">
						MinHub has not reviewed all of the sites linked to its website and is not responsible for the contents of any such linked site. The inclusion of any link does not imply endorsement by MinHub of the site. Use of any such linked website is at the user's own risk.
					</p>
				</section>

				<section className="mb-8">
					<h2 className="text-2xl font-semibold mb-4">11. Modifications</h2>
					<p className="mb-4">
						MinHub may revise these terms of service for its website at any time without notice. By using this website you are agreeing to be bound by the then current version of these terms of service.
					</p>
				</section>

				<section className="mb-8">
					<h2 className="text-2xl font-semibold mb-4">12. Governing Law</h2>
					<p className="mb-4">
						These terms and conditions are governed by and construed in accordance with the laws of the United States and you irrevocably submit to the exclusive jurisdiction of the courts in that location.
					</p>
				</section>

				<section className="mb-8">
					<h2 className="text-2xl font-semibold mb-4">13. Contact Information</h2>
					<p className="mb-4">
						If you have any questions about these Terms of Service, please contact us through our support channels.
					</p>
				</section>
			</div>
		</div>
	)
}
